import { Brand } from '@/components/Brand.tsx';
import { cn } from '@/lib/cn.ts';
import { VENUE_NAME } from '@okn/shared';
import { LogOut } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { NAV_GROUPS } from './nav.ts';

type Props = {
  adminName: string;
  onLogout: () => void;
  onNavigate?: () => void;
};

export function Sidebar({ adminName, onLogout, onNavigate }: Props) {
  return (
    <aside className="flex h-dvh flex-col border-r border-border bg-bg-elevated/80">
      <div className="flex items-center gap-3 border-b border-border px-5 py-5">
        <Brand height={22} />
        <span className="h-5 w-px bg-border-strong" />
        <div className="leading-tight">
          <p className="text-[10px] uppercase tracking-[0.16em] text-text-faint">Yönetim</p>
          <p className="text-[11px] text-text-muted">Paneli</p>
        </div>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.id}>
            <p className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] text-text-faint">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-accent-quiet text-text'
                        : 'text-text-muted hover:bg-surface hover:text-text',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-full bg-accent transition-opacity',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                        aria-hidden="true"
                      />
                      <item.icon
                        className={cn(
                          'h-[18px] w-[18px] shrink-0 transition-colors',
                          isActive ? 'text-accent' : 'text-text-faint group-hover:text-text-muted',
                        )}
                        strokeWidth={1.75}
                      />
                      {item.label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <footer className="border-t border-border px-4 py-4">
        <p className="truncate text-[10px] uppercase tracking-wider text-text-faint">
          {VENUE_NAME}
        </p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-quiet font-display text-[13px] text-accent">
              {adminName.trim().charAt(0).toUpperCase() || 'A'}
            </span>
            <span className="truncate text-sm text-text">{adminName}</span>
          </div>
          <button
            type="button"
            onClick={onLogout}
            aria-label="Çıkış yap"
            title="Çıkış yap"
            className="shrink-0 rounded-md p-2 text-text-faint transition-colors hover:bg-surface hover:text-danger"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </footer>
    </aside>
  );
}
